import { useState, useEffect } from 'react';

import { useDebouncedCallback } from 'use-debounce';

import { Icon } from '@/new/components/icon';

interface SearchBarProps {
  onSearch: (query: string) => void;
  onNext: () => void;
  onPrevious: () => void;
  onClose: () => void;
  hasResults: boolean;
  currentMatch: number;
  /** Total occurrences, or null when there are too many results to count ('?'). */
  totalMatches: number | null;
  isLoading?: boolean;
  initialQuery?: string;
}

export const SearchBar = ({
  onSearch,
  onNext,
  onPrevious,
  onClose,
  hasResults,
  currentMatch,
  totalMatches,
  isLoading = false,
  initialQuery = '',
}: SearchBarProps) => {
  const [query, setQuery] = useState(initialQuery);

  const debouncedSearch = useDebouncedCallback((value: string) => {
    onSearch(value);
  }, 300);

  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery);
    }
  }, [initialQuery]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    debouncedSearch(newQuery);
  };

  return (
    <div className="flex w-fit items-center gap-1 rounded-md border bg-white px-2 py-1.5 shadow-lg/3">
      <input
        type="text"
        value={query}
        onChange={handleInputChange}
        placeholder="Search..."
        className="w-40 rounded border-0 px-2 py-0.5 text-xs focus:outline-none"
        autoFocus
      />

      {isLoading ? (
        <Icon className="lucide--loader-2 mx-1 h-3.5 w-3.5 animate-spin text-gray-400" />
      ) : (
        hasResults && (
          <>
            <span className="px-1 text-xs text-gray-600">
              {currentMatch}/{totalMatches ?? '?'}
            </span>

            <div className="mx-1 h-4 w-px bg-gray-300" />

            <button onClick={onPrevious} className="rounded p-0.5 hover:bg-gray-100">
              <Icon className="lucide--chevron-up h-3 w-3" />
            </button>

            <button onClick={onNext} className="rounded p-0.5 hover:bg-gray-100">
              <Icon className="lucide--chevron-down h-3 w-3" />
            </button>
          </>
        )
      )}

      <button onClick={onClose} className="rounded p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900">
        <Icon className="lucide--x h-3 w-3" />
      </button>
    </div>
  );
};
